// Pure display helpers. No React, no imports from api/ — template §2's `utils/` rule.
//
// Formatting mirrors the backend's `reportExportService.formatValue` so a number reads identically
// on screen and in an exported file. Divergence between the two is the most common way a user
// concludes the export is "wrong".
//
// ═════════════════════════════════════════════════════════════════════════════
// THE APPLICATION TIMEZONE — Indian Standard Time
// ═════════════════════════════════════════════════════════════════════════════
//
// THE VVR ERP IS AN IST APPLICATION. Users enter IST, users see IST, and every date a user reads is
// the IST calendar date. This file is the frontend's single authority on that, mirroring the
// backend's `functions/src/constants/appTime.js`; no screen may format a date any other way, and none
// may reach for `toISOString()` or the browser's local zone to render one.
//
//     API sends an absolute instant  ->  formatted HERE  ->  IST on screen
//     IST typed into a picker        ->  converted HERE  ->  absolute instant sent to the API
//
// ── WHY NOT `toISOString()`, WHICH IS WHAT THIS REPLACES ────────────────────
// `new Date(v).toISOString().slice(0, 16)` renders the UTC wall clock. For an Indian user that is
// 5h30m early: a deadline of 21-08-2026 04:05 PM displayed as "2026-08-21 10:35". Every date on every
// screen was wrong in the same direction, which is precisely why nobody noticed — the times were
// self-consistent, just uniformly not the times anyone had entered.
//
// ── AND WHY NOT THE BROWSER'S LOCAL ZONE ────────────────────────────────────
// `toLocaleString()` with no `timeZone` renders wherever the reader's laptop happens to be set. A
// deadline would read differently for a colleague travelling, or for anyone whose machine clock is
// misconfigured, and the record would appear to change without anybody editing it. The zone is
// therefore ALWAYS passed explicitly, and IST is a property of the application, not of the reader.
//
// ── THE INSTANT IS NEVER TOUCHED ────────────────────────────────────────────
// These helpers convert between an absolute instant and an IST wall clock. They never add or subtract
// an offset by hand, so there is no way for +05:30 to be applied twice: `Intl` resolves the zone from
// the instant, and `fromDateTimeLocalValue` is the exact inverse of `toDateTimeLocalValue`.

/** The IANA zone identifier — THE authoritative value, matching the backend constant. */
export const APP_TIMEZONE = "Asia/Kolkata";

/** What a user is shown when the zone must be named in words. */
export const APP_TIMEZONE_LABEL = "IST";

/** The full phrase for help text beneath a date field. */
export const APP_TIMEZONE_DESCRIPTION = "Indian Standard Time (IST, UTC+05:30)";

// `en-GB` + `h23` is chosen for PARSING stability — it yields zero-padded numeric parts. The
// user-facing string is composed by hand below, so the locale never leaks into what anyone reads.
const PARTS_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const pad = (n) => String(n).padStart(2, "0");

/** The IST wall-clock fields of an instant, or `null` for an absent/invalid value. */
function zonedParts(value) {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const out = {};
  for (const p of PARTS_FORMATTER.formatToParts(date)) {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  if (out.hour === 24) out.hour = 0;
  return out;
}

/** `21-08-2026` — the IST calendar date of an instant. */
export function formatDate(value) {
  const p = zonedParts(value);
  return p ? `${pad(p.day)}-${pad(p.month)}-${p.year}` : EMPTY;
}

/** `04:05 PM` — the IST wall-clock time of an instant. */
export function formatTime(value) {
  const p = zonedParts(value);
  if (!p) return EMPTY;
  const suffix = p.hour < 12 ? "AM" : "PM";
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${pad(hour12)}:${pad(p.minute)} ${suffix}`;
}

/** `21-08-2026 04:05 PM` — the standard user-facing timestamp. */
export function formatDateTime(value) {
  const p = zonedParts(value);
  return p ? `${formatDate(value)} ${formatTime(value)}` : EMPTY;
}

/**
 * `21-08-2026 04:05 PM IST` — the zone stated explicitly.
 *
 * Used where a timestamp appears without a nearby "(IST)" column header to explain it: history rows,
 * audit trails and anywhere a value might be copied out of context.
 */
export function formatDateTimeLabeled(value) {
  const p = zonedParts(value);
  return p ? `${formatDateTime(value)} ${APP_TIMEZONE_LABEL}` : EMPTY;
}

/**
 * An instant -> the `yyyy-MM-ddTHH:mm` string an `<input type="datetime-local">` expects, in IST.
 *
 * The input element has no timezone of its own — it holds a bare wall clock. Filling it with the IST
 * wall clock is what makes the field mean IST to the person typing in it.
 */
export function toDateTimeLocalValue(value) {
  const p = zonedParts(value);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}` : "";
}

/**
 * The inverse: a `datetime-local` wall clock READ AS IST -> the absolute instant to send to the API.
 *
 * ⚠ THIS IS THE FUNCTION THE ORIGINAL DEFECT LIVED IN. Every form previously built the wire value as
 * `new Date(`${value}:00Z`).toISOString()` — appending `Z`, which declares the user's IST entry to be
 * UTC. A 04:05 PM deadline was stored as 16:05Z, i.e. 09:35 PM IST, 5h30m late. The offset is now
 * resolved FROM THE ZONE rather than assumed, and it is applied exactly once.
 *
 * Returns "" for an empty field so a caller can distinguish "not filled in" from a real instant.
 */
export function fromDateTimeLocalValue(value) {
  if (!value) return "";
  const m = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return "";
  const [, y, mo, d, h, mi, s] = m;
  const asIfUtc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s || 0));
  // Resolve the zone's offset AT that instant rather than hard-coding 330: guess, then re-check.
  // For IST (no daylight saving) the second pass never moves, but the shape keeps the maths honest.
  const offsetAt = (t) => {
    const p = zonedParts(new Date(t));
    return (Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(t / 1000) * 1000) / 60000;
  };
  const first = offsetAt(asIfUtc - 330 * 60000);
  const instant = asIfUtc - first * 60000;
  const settled = offsetAt(instant);
  return new Date(settled === first ? instant : asIfUtc - settled * 60000).toISOString();
}

export const COLUMN_TYPE = {
  STRING: "String",
  NUMBER: "Number",
  CURRENCY: "Currency",
  PERCENT: "Percent",
  DATE: "Date",
  DATETIME: "DateTime",
  BOOLEAN: "Boolean",
  DURATION_DAYS: "DurationDays",
};

const NUMERIC = [COLUMN_TYPE.NUMBER, COLUMN_TYPE.CURRENCY, COLUMN_TYPE.PERCENT, COLUMN_TYPE.DURATION_DAYS];

export const isNumericType = (type) => NUMERIC.includes(type);

/**
 * Absence renders as an em-dash, never as 0 or "N/A".
 *
 * This is load-bearing, not cosmetic. Module 13's closure §16 is explicit: "Expect an incomplete
 * trail… Module 14 must render absence as absence, never as a default." A missing reason and a
 * reason of zero are different facts, and a report that conflates them is lying quietly.
 */
export const EMPTY = "—";

export function formatValue(value, type) {
  if (value === null || value === undefined || value === "") return EMPTY;

  switch (type) {
    case COLUMN_TYPE.CURRENCY:
      return Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    case COLUMN_TYPE.NUMBER:
      return Number(value).toLocaleString("en-IN");
    case COLUMN_TYPE.PERCENT:
      return `${Number(value)}%`;
    case COLUMN_TYPE.DURATION_DAYS:
      return `${Number(value)} d`;
    // IST, not the UTC slice these used to return. A report column and the screen beside it must
    // agree, and both must agree with what the user typed.
    case COLUMN_TYPE.DATE:
      return formatDate(value);
    case COLUMN_TYPE.DATETIME:
      return formatDateTime(value);
    case COLUMN_TYPE.BOOLEAN:
      return value ? "Yes" : "No";
    default:
      return String(value);
  }
}

/** Compact form for KPI cards, where 1,20,00,000 does not fit. */
export function compactNumber(value) {
  if (value === null || value === undefined) return EMPTY;
  const n = Number(value);
  if (Number.isNaN(n)) return EMPTY;
  if (Math.abs(n) >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (Math.abs(n) >= 100000) return `${(n / 100000).toFixed(2)} L`;
  if (Math.abs(n) >= 1000) return n.toLocaleString("en-IN");
  return String(n);
}

export function formatKpi(value, type) {
  if (value === null || value === undefined) return EMPTY;
  return type === COLUMN_TYPE.CURRENCY ? compactNumber(value) : formatValue(value, type);
}

/**
 * ISO `yyyy-mm-dd` for date inputs; the API accepts and returns ISO throughout.
 *
 * THE IST CALENDAR DAY, not the UTC one. These two differ for every instant between 18:30Z and
 * midnight — a task created at 00:15 IST would otherwise pre-fill a filter with the previous day.
 */
export const toIsoDate = (d) => {
  const p = zonedParts(d);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}` : "";
};

export function errorMessage(err, fallback = "Something went wrong") {
  return err?.response?.data?.message || err?.message || fallback;
}
